import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';

import { Icon } from '@/core/components/Icons';
import { Paragraph } from '@/core/components/Typography';

import { TokenUsageBar } from '@/features/ChatBot/components/TokenUsageBar';
import { tokenUsagePercentageSelector, tokenUsageSelector } from '@/features/ChatBot/store';

import {
  BotMetaDivider,
  MessageBubble,
  MessageGroup,
  MessageMeta,
  MessageMetaRow,
  MessageRow,
} from './ChatContent.styles';
import { Loader } from './Loader';
import { MarkdownText } from './MarkdownText';
import { BotMessageProps } from './BotMessage.types';

const resolveContent = ({ tokens, text }: Pick<BotMessageProps, 'tokens' | 'text'>) => {
  if (tokens && tokens.length > 0) {
    return tokens.join('');
  }

  return text ?? '';
};

const FIRST_H1_REGEX = /^(#\s[^\n]*)(\n|$)/m;

const splitAtFirstH1 = (content: string): { head: string; rest: string } | null => {
  const match = FIRST_H1_REGEX.exec(content);

  if (!match) return null;

  const endOfHeading = match.index + match[1].length;

  return {
    head: content.slice(0, endOfHeading),
    rest: content.slice(endOfHeading),
  };
};

export const BotMessage = ({ text, tokens, isGenerating, timestamp, error }: BotMessageProps) => {
  const { colors } = useTheme();
  const tokenUsage = useSelector(tokenUsageSelector);
  const tokenUsagePercentage = useSelector(tokenUsagePercentageSelector);

  const content = resolveContent({
    tokens,
    text,
  });

  const messageText = isGenerating ? 'AI Analyst is processing, please wait...' : timestamp;
  const showError = !!error;

  const split = tokenUsage ? splitAtFirstH1(content) : null;

  const analysisTokens = content.length === 0 ? undefined : Math.ceil(content.length / 4);

  return (
    <MessageGroup>
      <MessageRow>
        <MessageBubble>
          {showError ? (
            <Paragraph type="small-normal">{error}</Paragraph>
          ) : (
            <>
              {split && tokenUsage ? (
                <>
                  <MarkdownText
                    text={split.head}
                    paragraphType="normal"
                  />
                  <TokenUsageBar
                    percentage={tokenUsagePercentage}
                    usedTokens={tokenUsage.usedTokens}
                    remainingTokens={tokenUsage.remainingTokens}
                    analysisTokens={analysisTokens}
                  />
                  <MarkdownText
                    text={split.rest}
                    paragraphType="normal"
                  />
                </>
              ) : (
                <MarkdownText
                  text={content}
                  paragraphType="normal"
                />
              )}
              {isGenerating && <Loader />}
            </>
          )}
        </MessageBubble>
      </MessageRow>
      {timestamp && <BotMetaDivider />}
      <MessageMetaRow>
        <Icon
          name="Robot"
          size={15}
          color={colors.secondaryText}
        />
        <MessageMeta>
          <Paragraph
            type="tiny"
            color={colors.secondaryText}
          >
            {messageText}
          </Paragraph>
        </MessageMeta>
      </MessageMetaRow>
    </MessageGroup>
  );
};
